import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Download, Send, Eye, Edit3,
  FileText, Paperclip, CheckCircle, AlertTriangle,
  Clock, RefreshCw, X, ChevronDown, 
  Lock, MapPin, Scroll, ShieldAlert, Search,
  ClipboardCheck,  ExternalLink,
   MailCheck, BadgeCheck
} from 'lucide-react';
import {
  getDossier, getRapport, createRapport,
  updateRapport, soumettreRapport,
  corrigerRapport, envoyerRapport,
  telechargerPdfRapport,
} from '../api/dossiers';
import DocumentUpload from '../components/DocumentUpload';
import useAuthStore   from '../store/authStore';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
dayjs.locale('fr');

/* ─── PALETTE ─── */
const C = {
  sand:      '#f5ede0', sandDark: '#e8d8c0',
  terra:     '#c2521a', terraLt:  '#e0784a', terraDk: '#8a3510',
  forest:    '#1a3d28', forestMid:'#244d35', forestLt:'#2f6645',
  gold:      '#c8902a', goldLt:   '#e8b84a',
  ink:       '#0e1a10', mist:     '#6e8070', cream:   '#faf6ef',
  purple:    '#7c6bbf', blue:     '#2a8fa8',
};

/* ─── SECTIONS ─── */
const SECTIONS = [
  { key:'identification', label:'Identification du terrain',  help:'Localisation précise, superficie, références cadastrales, numéro de lot…', icon: MapPin,         color: C.terra,     light:'rgba(194,82,26,.12)'  },
  { key:'historique',     label:'Historique de propriété',   help:'Chaîne des propriétaires successifs, actes antérieurs, litiges connus…',   icon: Scroll,         color: C.purple,    light:'rgba(124,107,191,.12)'},
  { key:'etat_terrain',   label:'État du terrain',           help:'Nature du sol, présence de bornes, clôtures, constructions existantes…',    icon: FileText,       color: C.blue,      light:'rgba(42,143,168,.12)' },
  { key:'risques',        label:'Risques identifiés',        help:'Litiges en cours, droits de passage, zones inondables, servitudes…',        icon: ShieldAlert,    color: C.gold,      light:'rgba(200,144,42,.12)' },
  { key:'avis_expert',    label:"Avis de l'expert",          help:"Analyse personnelle de l'agent enquêteur, points de vigilance…",            icon: Search,         color: C.forestLt,  light:'rgba(47,102,69,.12)'  },
  { key:'conclusion',     label:'Conclusion & recommandation',help:'Recommandation finale : acquérir, négocier, éviter…',                      icon: ClipboardCheck, color: C.forestMid, light:'rgba(36,77,53,.12)'   },
];

const STATUT_STYLE = {
  BROUILLON: { bg:'rgba(110,128,112,.1)', color:C.mist,      dot:C.mist,     label:'Brouillon' },
  SOUMIS:    { bg:'rgba(200,144,42,.12)', color:'#8a6010',   dot:C.gold,     label:'Soumis'    },
  CORRIGE:   { bg:'rgba(194,82,26,.12)',  color:C.terraDk,   dot:C.terra,    label:'À corriger'},
  ENVOYE:    { bg:'rgba(47,102,69,.12)',  color:C.forestLt,  dot:C.forestLt, label:'Envoyé ✓'  },
};
const RESULTAT_STYLE = {
  EN_ATTENTE: { bg:'rgba(200,144,42,.08)',  border:'rgba(200,144,42,.25)', color:'#8a6010',  label:'En attente',               icon:'⏳' },
  VALIDE:     { bg:'rgba(47,102,69,.08)',   border:'rgba(47,102,69,.25)',  color:C.forestLt, label:'Terrain validé — achat sécurisé', icon:'✓'  },
  LITIGE:     { bg:'rgba(194,82,26,.08)',   border:'rgba(194,82,26,.25)',  color:C.terraDk,  label:'Litige détecté',           icon:'⚠'  },
  RISQUE:     { bg:'rgba(200,144,42,.08)',  border:'rgba(200,144,42,.3)',  color:C.terra,    label:'Risque identifié',         icon:'🔶' },
};

/* ─── CSS ─── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500;600;700&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
body{font-family:'DM Sans',sans-serif;}

.rp-page{min-height:100vh;padding:28px 36px 60px;}
@media(max-width:768px){.rp-page{padding:16px 14px 48px;}}

/* ── HEADER ── */
.rp-topbar{display:flex;align-items:center;gap:12px;margin-bottom:8px;flex-wrap:wrap;}
.btn-bk{display:inline-flex;align-items:center;gap:7px;padding:8px 15px;border-radius:11px;border:1.5px solid rgba(26,61,40,.13);background:transparent;font:600 .83rem 'DM Sans',sans-serif;color:${C.mist};cursor:pointer;transition:all .2s;}
.btn-bk:hover{border-color:${C.forest};color:${C.forest};background:rgba(26,61,40,.03);}
.rp-title-row{display:flex;align-items:center;gap:12px;margin-bottom:22px;flex-wrap:wrap;}
.rp-title{font-family:'Cormorant Garamond',serif;font-size:clamp(1.5rem,2.8vw,2.2rem);font-weight:700;letter-spacing:-.03em;color:${C.ink};}
.rp-title em{font-style:italic;color:${C.terra};}
.stag{display:inline-flex;align-items:center;gap:5px;padding:4px 12px;border-radius:999px;font-size:.71rem;font-weight:700;white-space:nowrap;}
.stag-dot{width:5px;height:5px;border-radius:50%;flex:none;}

/* ── LAYOUT ── */
.rp-body{display:grid;grid-template-columns:1fr 300px;gap:20px;align-items:start;}
@media(max-width:1050px){.rp-body{grid-template-columns:1fr;}}

/* ── CORRECTIONS BANNER ── */
.corrections-banner{
  background:rgba(200,144,42,.07);border:1.5px solid rgba(200,144,42,.28);
  border-radius:16px;padding:16px 18px;margin-bottom:20px;
  display:flex;align-items:flex-start;gap:12px;
}
.cb-icon{width:36px;height:36px;border-radius:10px;background:rgba(200,144,42,.15);display:grid;place-items:center;color:${C.gold};flex:none;}
.cb-title{font-size:.72rem;font-weight:800;letter-spacing:.14em;text-transform:uppercase;color:${C.gold};margin-bottom:4px;}
.cb-text{font-size:.86rem;line-height:1.7;color:#8a6010;}

/* ── ALERT ── */
.alert{display:flex;align-items:flex-start;gap:10px;border-radius:13px;padding:12px 16px;font-size:.84rem;line-height:1.65;margin-bottom:18px;border:1px solid transparent;position:relative;overflow:hidden;}
.alert::before{content:'';position:absolute;left:0;top:0;bottom:0;width:3px;}
.alert-s{background:rgba(47,102,69,.07);border-color:rgba(47,102,69,.2);color:${C.forestLt};}
.alert-s::before{background:${C.forestLt};}
.alert-e{background:rgba(194,82,26,.07);border-color:rgba(194,82,26,.2);color:${C.terraDk};}
.alert-e::before{background:${C.terra};}
.alert-x{margin-left:auto;background:transparent;border:none;cursor:pointer;opacity:.6;transition:opacity .2s;flex:none;}
.alert-x:hover{opacity:1;}

/* ── TRAME CARD ── */
.trame-card{background:white;border-radius:22px;border:1px solid rgba(26,61,40,.07);box-shadow:0 4px 20px rgba(26,61,40,.06);overflow:hidden;}
.trame-head{padding:20px 24px 18px;border-bottom:1px solid rgba(26,61,40,.06);display:flex;align-items:center;gap:10px;background:linear-gradient(135deg,rgba(26,61,40,.025),transparent);}
.trame-icon{width:36px;height:36px;border-radius:10px;background:rgba(26,61,40,.09);display:grid;place-items:center;color:${C.forest};flex:none;}
.trame-title{font-family:'Cormorant Garamond',serif;font-size:1.2rem;font-weight:700;color:${C.ink};}
.trame-body{padding:24px;}

/* ── SECTION BLOCKS ── */
.section-blocks{display:grid;gap:16px;margin-bottom:22px;}
.section-block{border-radius:16px;border:1.5px solid rgba(26,61,40,.07);overflow:hidden;transition:border-color .2s,box-shadow .2s;}
.section-block:hover{border-color:rgba(26,61,40,.14);box-shadow:0 4px 16px rgba(26,61,40,.06);}
.sb-header{
  display:flex;align-items:center;gap:10px;padding:14px 16px;
  cursor:pointer;user-select:none;transition:background .2s;
}
.sb-header:hover{background:rgba(26,61,40,.02);}
.sb-icon{width:32px;height:32px;border-radius:9px;display:grid;place-items:center;flex:none;}
.sb-label{font-size:.9rem;font-weight:700;color:${C.ink};flex:1;}
.sb-help{font-size:.74rem;color:${C.mist};margin-top:1px;}
.sb-count{font-size:.7rem;color:rgba(110,128,112,.45);font-weight:600;white-space:nowrap;}
.sb-chevron{color:${C.mist};transition:transform .25s;flex:none;}
.sb-chevron.open{transform:rotate(180deg);}
.sb-body{padding:0 16px 16px;}
.sb-ta{
  width:100%;border:1.5px solid rgba(26,61,40,.1);border-radius:12px;
  background:${C.cream};color:${C.ink};padding:12px 14px;
  font:400 .9rem 'DM Sans',sans-serif;outline:none;resize:vertical;
  min-height:110px;line-height:1.75;transition:border-color .2s,box-shadow .2s,background .2s;
}
.sb-ta:focus{border-color:${C.terra};box-shadow:0 0 0 4px rgba(194,82,26,.09);background:white;}
.sb-ta:disabled{opacity:.65;cursor:not-allowed;background:rgba(26,61,40,.025);}
.sb-ta::placeholder{color:rgba(110,128,112,.35);}
/* filled indicator */
.sb-filled{width:7px;height:7px;border-radius:50%;flex:none;}

/* ── RESULTAT SELECT ── */
.resultat-wrap{margin-bottom:22px;}
.r-label{font-size:.68rem;font-weight:800;letter-spacing:.14em;text-transform:uppercase;color:${C.mist};margin-bottom:8px;display:flex;align-items:center;gap:7px;}
.r-label::before{content:'';display:inline-block;width:16px;height:2px;border-radius:999px;background:${C.terra};}
.resultat-options{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
@media(max-width:500px){.resultat-options{grid-template-columns:1fr;}}
.r-option{
  display:flex;align-items:center;gap:10px;padding:12px 14px;
  border-radius:13px;border:1.5px solid rgba(26,61,40,.1);
  background:${C.cream};cursor:pointer;transition:all .2s;
  font:500 .86rem 'DM Sans',sans-serif;color:${C.mist};
}
.r-option:hover:not(:disabled){background:white;border-color:rgba(26,61,40,.18);}
.r-option.selected{font-weight:700;}
.r-option:disabled{opacity:.5;cursor:not-allowed;}
.r-option-dot{width:10px;height:10px;border-radius:50%;flex:none;}

/* ── ACTION BUTTONS ── */
.trame-actions{display:flex;gap:10px;flex-wrap:wrap;padding-top:18px;border-top:1px solid rgba(26,61,40,.06);}
.btn-draft{
  display:inline-flex;align-items:center;gap:8px;padding:11px 20px;border-radius:12px;
  border:1.5px solid rgba(26,61,40,.18);background:transparent;color:${C.forest};
  font:600 .88rem 'DM Sans',sans-serif;cursor:pointer;transition:all .2s;
}
.btn-draft:hover:not(:disabled){background:rgba(26,61,40,.04);border-color:${C.forest};}
.btn-draft:disabled{opacity:.5;cursor:not-allowed;}
.btn-submit{
  display:inline-flex;align-items:center;gap:8px;padding:11px 22px;border-radius:12px;
  background:${C.forest};color:${C.sand};border:none;
  font:700 .88rem 'DM Sans',sans-serif;cursor:pointer;
  box-shadow:0 8px 22px rgba(26,61,40,.2);transition:transform .25s,box-shadow .25s;
  position:relative;overflow:hidden;
}
.btn-submit::before{content:'';position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,.08),transparent);transform:translateX(-100%);transition:transform .5s;}
.btn-submit:hover::before{transform:translateX(100%);}
.btn-submit:hover:not(:disabled){transform:translateY(-2px);box-shadow:0 14px 30px rgba(26,61,40,.27);}
.btn-submit:disabled{opacity:.5;cursor:not-allowed;transform:none;}
.btn-wide{
  display:flex;align-items:center;justify-content:center;gap:8px;width:100%;
  padding:11px;border-radius:12px;font:700 .86rem 'DM Sans',sans-serif;
  border:none;cursor:pointer;transition:transform .25s,box-shadow .25s;
  position:relative;overflow:hidden;margin-bottom:8px;
}
.btn-wide:last-child{margin-bottom:0;}
.btn-wide::before{content:'';position:absolute;inset:0;background:linear-gradient(90deg,transparent,rgba(255,255,255,.08),transparent);transform:translateX(-100%);transition:transform .5s;}
.btn-wide:hover::before{transform:translateX(100%);}
.btn-wide:hover:not(:disabled){transform:translateY(-2px);}
.btn-wide:disabled{opacity:.5;cursor:not-allowed;transform:none;}
.bw-forest{background:${C.forest};color:${C.sand};box-shadow:0 7px 20px rgba(26,61,40,.2);}
.bw-terra{background:linear-gradient(135deg,${C.terra},${C.terraLt});color:white;box-shadow:0 7px 20px rgba(194,82,26,.22);}
.bw-gold{background:linear-gradient(135deg,${C.gold},${C.goldLt});color:${C.ink};box-shadow:0 7px 20px rgba(200,144,42,.22);}
.bw-outline{background:transparent;border:1.5px solid rgba(26,61,40,.15);color:${C.forest};box-shadow:none;}
.bw-outline:hover:not(:disabled){background:rgba(26,61,40,.04);border-color:${C.forest};}
.bw-green{background:linear-gradient(135deg,${C.forestMid},${C.forestLt});color:${C.sand};box-shadow:0 7px 20px rgba(26,61,40,.2);}

/* ── WAITING STATE ── */
.waiting-state{text-align:center;padding:20px 10px;display:grid;gap:10px;justify-items:center;}
.ws-icon{width:48px;height:48px;border-radius:50%;background:rgba(200,144,42,.1);display:grid;place-items:center;color:${C.gold};animation:wblink 2s ease-in-out infinite;}
@keyframes wblink{0%,100%{box-shadow:0 0 0 0 rgba(200,144,42,.5);}50%{box-shadow:0 0 0 8px rgba(200,144,42,0);}}
.ws-title{font-family:'Cormorant Garamond',serif;font-size:1.1rem;font-weight:700;color:${C.ink};}
.ws-sub{font-size:.8rem;color:${C.mist};line-height:1.65;max-width:220px;}

/* ── SENT STATE ── */
.sent-state{text-align:center;padding:16px 10px;display:grid;gap:10px;justify-items:center;}
.sent-icon{width:52px;height:52px;border-radius:50%;background:rgba(47,102,69,.1);display:grid;place-items:center;color:${C.forestLt};box-shadow:0 8px 22px rgba(47,102,69,.18);}
.sent-title{font-family:'Cormorant Garamond',serif;font-size:1.05rem;font-weight:700;color:${C.forestLt};}
.sent-date{font-size:.76rem;color:${C.mist};line-height:1.6;}
.sent-by{font-size:.74rem;color:rgba(110,128,112,.55);}

/* ── SIDE CARDS ── */
.side-stack{display:grid;gap:16px;}
.sc{background:white;border-radius:20px;border:1px solid rgba(26,61,40,.07);box-shadow:0 3px 14px rgba(26,61,40,.05);overflow:hidden;}
.sc.bdr-gold{border-color:rgba(200,144,42,.28);}
.sc.bdr-green{border-color:rgba(47,102,69,.28);}
.sc.bdr-terra{border-color:rgba(194,82,26,.22);}
.sch{padding:16px 18px 14px;border-bottom:1px solid rgba(26,61,40,.06);display:flex;align-items:center;gap:9px;}
.sch-icon{width:32px;height:32px;border-radius:9px;display:grid;place-items:center;flex:none;}
.sch-title{font-family:'Cormorant Garamond',serif;font-size:1.05rem;font-weight:700;color:${C.ink};}
.scb{padding:16px 18px;}
.sc-sub{font-size:.8rem;color:${C.mist};line-height:1.65;margin-bottom:14px;}

/* corrections textarea */
.corr-ta{width:100%;border:1.5px solid rgba(200,144,42,.25);border-radius:11px;background:rgba(200,144,42,.04);color:${C.ink};padding:10px 13px;font:400 .87rem 'DM Sans',sans-serif;outline:none;resize:vertical;min-height:90px;transition:border-color .2s,box-shadow .2s;margin-bottom:12px;}
.corr-ta:focus{border-color:${C.gold};box-shadow:0 0 0 3px rgba(200,144,42,.12);background:white;}
.corr-ta::placeholder{color:rgba(110,128,112,.35);}

/* terrain info strip */
.terrain-strip{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:14px;}
.ts-item{background:${C.cream};border-radius:10px;padding:9px 11px;border:1px solid rgba(26,61,40,.06);}
.ts-lbl{font-size:.62rem;font-weight:800;letter-spacing:.14em;text-transform:uppercase;color:${C.mist};margin-bottom:2px;}
.ts-val{font-size:.84rem;font-weight:600;color:${C.ink};}

/* spin */
.spin{animation:spin .8s linear infinite;}
@keyframes spin{to{transform:rotate(360deg);}}

/* ── MODAL OVERLAY ── */
.modal-overlay{
  position:fixed;inset:0;background:rgba(14,26,16,.55);
  backdrop-filter:blur(8px);z-index:100;
  display:flex;align-items:center;justify-content:center;padding:20px;
}
.modal{
  background:white;border-radius:24px;
  width:100%;max-width:780px;max-height:90vh;
  display:flex;flex-direction:column;
  box-shadow:0 30px 80px rgba(14,26,16,.3);
  overflow:hidden;
}
.modal-head{
  padding:22px 26px 18px;border-bottom:1px solid rgba(26,61,40,.07);
  display:flex;align-items:center;gap:12px;flex:none;
  background:linear-gradient(135deg,${C.forest},${C.forestMid});
}
.modal-head-icon{width:40px;height:40px;border-radius:11px;background:rgba(245,237,224,.15);display:grid;place-items:center;color:${C.sand};flex:none;}
.modal-title{font-family:'Cormorant Garamond',serif;font-size:1.3rem;font-weight:700;color:${C.sand};}
.modal-ref{font-size:.78rem;color:rgba(245,237,224,.55);margin-top:1px;}
.modal-close{margin-left:auto;background:rgba(245,237,224,.1);border:none;width:32px;height:32px;border-radius:8px;display:grid;place-items:center;cursor:pointer;color:${C.sand};transition:background .2s;flex:none;}
.modal-close:hover{background:rgba(245,237,224,.2);}
.modal-body{flex:1;overflow-y:auto;padding:24px 26px;}
.modal-body::-webkit-scrollbar{width:4px;}
.modal-body::-webkit-scrollbar-thumb{background:rgba(26,61,40,.12);border-radius:999px;}
.modal-foot{padding:16px 26px;border-top:1px solid rgba(26,61,40,.07);display:flex;align-items:center;justify-content:space-between;gap:12px;flex:none;background:${C.cream};}

/* preview content */
.prev-hero{
  background:linear-gradient(135deg,${C.forest},${C.forestMid});
  border-radius:16px;padding:22px 24px;margin-bottom:20px;text-align:center;
  position:relative;overflow:hidden;
}
.prev-hero-pattern{position:absolute;inset:0;background-image:repeating-linear-gradient(0deg,rgba(245,237,224,.03) 0,rgba(245,237,224,.03) 1px,transparent 1px,transparent 40px),repeating-linear-gradient(90deg,rgba(245,237,224,.03) 0,rgba(245,237,224,.03) 1px,transparent 1px,transparent 40px);}
.prev-logo{font-family:'Cormorant Garamond',serif;font-size:1.6rem;font-weight:700;color:${C.sand};letter-spacing:-.02em;position:relative;}
.prev-logo em{color:${C.terraLt};font-style:normal;}
.prev-subtitle{font-size:.72rem;font-weight:800;letter-spacing:.2em;text-transform:uppercase;color:rgba(245,237,224,.5);margin-top:3px;position:relative;}
.prev-ref{background:rgba(245,237,224,.1);border:1px solid rgba(245,237,224,.18);border-radius:8px;padding:6px 16px;font-size:.82rem;font-weight:700;color:${C.sand};display:inline-block;margin-top:10px;position:relative;}
.prev-terrain{display:grid;grid-template-columns:repeat(2,1fr);gap:10px;margin-bottom:18px;}
.pt-item{background:${C.cream};border-radius:11px;padding:10px 13px;border:1px solid rgba(26,61,40,.07);}
.pt-lbl{font-size:.63rem;font-weight:800;letter-spacing:.14em;text-transform:uppercase;color:${C.mist};margin-bottom:2px;}
.pt-val{font-size:.86rem;font-weight:600;color:${C.ink};}
.prev-section{margin-bottom:16px;padding-bottom:16px;border-bottom:1px solid rgba(26,61,40,.07);}
.prev-section:last-of-type{border-bottom:none;}
.prev-section-label{font-size:.68rem;font-weight:800;letter-spacing:.16em;text-transform:uppercase;margin-bottom:7px;display:flex;align-items:center;gap:7px;}
.prev-section-text{font-size:.88rem;line-height:1.75;color:${C.forest};white-space:pre-wrap;}
.prev-result{padding:14px 16px;border-radius:13px;margin-bottom:18px;border:1.5px solid transparent;}
.prev-result-label{font-size:.66rem;font-weight:800;letter-spacing:.16em;text-transform:uppercase;margin-bottom:4px;}
.prev-result-value{font-family:'Cormorant Garamond',serif;font-size:1.15rem;font-weight:700;}
.prev-sigs{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin-top:10px;}
.prev-sig{text-align:center;padding-top:14px;border-top:1.5px solid rgba(26,61,40,.12);}
.prev-sig-role{font-size:.64rem;font-weight:800;letter-spacing:.14em;text-transform:uppercase;color:${C.mist};margin-bottom:3px;}
.prev-sig-name{font-size:.86rem;font-weight:600;color:${C.ink};}

/* confirm modal */
.confirm-body{padding:24px;}
.confirm-title{font-family:'Cormorant Garamond',serif;font-size:1.4rem;font-weight:700;color:${C.ink};margin-bottom:10px;}
.confirm-text{font-size:.88rem;line-height:1.7;color:${C.mist};}
.confirm-email{font-weight:700;color:${C.forest};}
.confirm-warning{display:flex;align-items:center;gap:8px;padding:10px 13px;border-radius:11px;background:rgba(194,82,26,.06);border:1px solid rgba(194,82,26,.18);font-size:.8rem;color:${C.terraDk};margin-top:12px;}
`;

/* ─── STAGGER ─── */
const STAGGER = { hidden:{}, visible:{ transition:{ staggerChildren:.06 } } };
const ITEM    = { hidden:{opacity:0,y:14}, visible:{opacity:1,y:0,transition:{duration:.48,ease:[.22,1,.36,1]}} };

/* ─── SECTION BLOCK ─── */
function SectionBlock({ section, value, onChange, disabled, isOpen, onToggle }) {
  const Icon = section.icon;
  const wordCount = value?.trim().split(/\s+/).filter(Boolean).length || 0;
  const filled = value?.trim().length > 0;

  return (
    <motion.div className="section-block"
      initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }}
      transition={{ duration:.4, ease:[.22,1,.36,1] }}>
      <div className="sb-header" onClick={onToggle}>
        <div className="sb-icon" style={{ background: section.light, color: section.color }}>
          <Icon size={15}/>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div className="sb-label">{section.label}</div>
          {!isOpen && <div className="sb-help">{section.help}</div>}
        </div>
        {filled && (
          <div className="sb-filled" style={{ background: section.color }} title="Section remplie"/>
        )}
        {isOpen && <span className="sb-count">{wordCount} mot{wordCount>1?'s':''}</span>}
        <div className={`sb-chevron${isOpen?' open':''}`}>
          <ChevronDown size={15}/>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div className="sb-body"
            initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }}
            exit={{ height:0, opacity:0 }}
            transition={{ duration:.3, ease:[.22,1,.36,1] }}>
            <div style={{ fontSize:'.76rem', color:C.mist, marginBottom:8, lineHeight:1.5 }}>
              {section.help}
            </div>
            <textarea className="sb-ta"
              value={value} onChange={e => onChange(e.target.value)}
              disabled={disabled}
              placeholder={disabled ? '—' : `Rédigez ici : ${section.label.toLowerCase()}…`}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ─── PREVIEW MODAL ─── */
function PreviewModal({ dossier, form, rapport, onClose, onSend }) {
  const res = RESULTAT_STYLE[form.resultat] || RESULTAT_STYLE.EN_ATTENTE;
  return (
    <motion.div className="modal-overlay"
      initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      onClick={e => e.target.className.includes('modal-overlay') && onClose()}>
      <motion.div className="modal"
        initial={{ opacity:0, scale:.94, y:20 }}
        animate={{ opacity:1, scale:1, y:0 }}
        exit={{ opacity:0, scale:.94, y:20 }}
        transition={{ duration:.38, ease:[.22,1,.36,1] }}>

        <div className="modal-head">
          <div className="modal-head-icon"><FileText size={18}/></div>
          <div>
            <div className="modal-title">Prévisualisation du rapport</div>
            <div className="modal-ref">Dossier {dossier?.reference}</div>
          </div>
          <button className="modal-close" onClick={onClose}><X size={15}/></button>
        </div>

        <div className="modal-body">
          {/* Hero */}
          <div className="prev-hero">
            <div className="prev-hero-pattern"/>
            <div className="prev-logo">Terra<em>Track</em></div>
            <div className="prev-subtitle">Rapport d'expertise foncière</div>
            <div className="prev-ref">N° {dossier?.reference}</div>
          </div>

          {/* Terrain */}
          <div className="prev-terrain">
            {[
              ['Localisation', dossier?.localisation],
              ['Ville / Pays', `${dossier?.ville||'—'}, ${dossier?.pays||'—'}`],
              ['Superficie',   dossier?.superficie ? `${dossier.superficie} m²` : '—'],
              ['Vendeur',      dossier?.nom_vendeur||'—'],
            ].map(([l,v]) => (
              <div key={l} className="pt-item">
                <div className="pt-lbl">{l}</div>
                <div className="pt-val">{v}</div>
              </div>
            ))}
          </div>

          {/* Sections */}
          {SECTIONS.map(s => form[s.key] && (
            <div key={s.key} className="prev-section">
              <div className="prev-section-label" style={{ color: s.color }}>
                <s.icon size={12}/> {s.label}
              </div>
              <div className="prev-section-text">{form[s.key]}</div>
            </div>
          ))}

          {/* Résultat */}
          <div className="prev-result" style={{ background: res.bg, borderColor: res.border }}>
            <div className="prev-result-label" style={{ color: res.color }}>Résultat final</div>
            <div className="prev-result-value" style={{ color: res.color }}>
              {res.icon} {res.label}
            </div>
          </div>

          {/* Signatures */}
          <div className="prev-sigs">
            <div className="prev-sig">
              <div className="prev-sig-role">Agent enquêteur</div>
              <div className="prev-sig-name">{rapport?.redige_par_nom||'—'}</div>
            </div>
            <div className="prev-sig">
              <div className="prev-sig-role">Validé par</div>
              <div className="prev-sig-name">Administrateur TerraTrack</div>
            </div>
          </div>
        </div>

        <div className="modal-foot">
          <button className="btn-draft" onClick={onClose}>Fermer</button>
          <button className="btn-submit" onClick={onSend}>
            <Send size={14}/> Envoyer au mandataire
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── CONFIRM MODAL ─── */
function ConfirmModal({ dossier, saving, onClose, onConfirm }) {
  return (
    <motion.div className="modal-overlay"
      initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      onClick={e => e.target.className.includes('modal-overlay') && onClose()}>
      <motion.div className="modal" style={{ maxWidth:440 }}
        initial={{ opacity:0, scale:.92, y:16 }}
        animate={{ opacity:1, scale:1, y:0 }}
        exit={{ opacity:0, scale:.92, y:16 }}
        transition={{ duration:.35, ease:[.22,1,.36,1] }}>
        <div className="modal-head">
          <div className="modal-head-icon"><MailCheck size={18}/></div>
          <div>
            <div className="modal-title">Confirmer l'envoi</div>
          </div>
          <button className="modal-close" onClick={onClose}><X size={15}/></button>
        </div>
        <div className="confirm-body">
          <div className="confirm-title">Envoyer le rapport ?</div>
          <p className="confirm-text">
            Vous allez envoyer le rapport d'expertise définitivement par email à{' '}
            <span className="confirm-email">{dossier?.mandataire?.email||'le mandataire'}</span>.
          </p>
          <div className="confirm-warning">
            <Lock size={13}/> Cette action est irréversible.
          </div>
        </div>
        <div className="modal-foot">
          <button className="btn-draft" onClick={onClose}>Annuler</button>
          <button className="btn-submit" onClick={onConfirm} disabled={saving}>
            {saving ? <RefreshCw size={14} className="spin"/> : <><MailCheck size={14}/> Confirmer l'envoi</>}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── MAIN ─── */
export default function RapportExpertise() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [dossier,     setDossier]     = useState(null);
  const [rapport,     setRapport]     = useState(null);
  const [form,        setForm]        = useState({
    identification:'', historique:'', etat_terrain:'',
    risques:'', avis_expert:'', conclusion:'', resultat:'EN_ATTENTE',
  });
  const [corrections, setCorrections] = useState('');
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState('');
  const [error,       setError]       = useState(null);
  const [success,     setSuccess]     = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [openSections, setOpenSections] = useState({ identification:true });

  const toggleSection = (key) => setOpenSections(p => ({ ...p, [key]: !p[key] }));
  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const fetchData = async () => {
    try {
      const [d, r] = await Promise.all([getDossier(id), getRapport(id)]);
      setDossier(d.data);
      const rd = r.data.results?.[0] || r.data[0] || null;
      if (rd) {
        setRapport(rd);
        setForm({
          identification: rd.identification||'',
          historique:     rd.historique||'',
          etat_terrain:   rd.etat_terrain||'',
          risques:        rd.risques||'',
          avis_expert:    rd.avis_expert||'',
          conclusion:     rd.conclusion||'',
          resultat:       rd.resultat||'EN_ATTENTE',
        });
        setCorrections(rd.corrections_admin||'');
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, [id]);

  const run = async (key, fn) => {
    setSaving(key); setError(null);
    try { await fn(); await fetchData(); }
    catch(e) { setError(typeof e.response?.data==='object' ? JSON.stringify(e.response.data,null,2) : (e.response?.data||'Erreur inattendue.')); }
    finally { setSaving(''); }
  };

  const handleSave = async () => {
    if (rapport) await updateRapport(rapport.id, { ...form, dossier: id });
    else await createRapport({ ...form, dossier: id });
    setSuccess('Rapport sauvegardé.');
  };

  const handleSoumettre = () => run('soumettre', async () => {
    await handleSave();
    const r = rapport || (await getRapport(id)).data[0];
    await soumettreRapport(r?.id);
    setSuccess("Rapport soumis à l'administrateur.");
  });

  const handleSaveDraft   = () => run('draft',   () => handleSave());
  const handleCorrections = () => run('correct', () => corrigerRapport(rapport.id, { corrections_admin: corrections }).then(()=>setSuccess('Corrections envoyées.')));
  const handleEnvoyer     = () => run('envoyer', () => envoyerRapport(rapport.id).then(()=>{ setSuccess('Rapport envoyé par email.'); setConfirmOpen(false); }));

  const handlePdf = async () => {
    try {
      const res  = await telechargerPdfRapport(rapport.id);
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const a    = document.createElement('a');
      a.href     = url; a.setAttribute('download', `rapport_${dossier?.reference}.pdf`);
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { setError('Erreur lors du téléchargement du PDF.'); }
  };

  const isReadOnly = user?.role === 'MANDATAIRE' ||
    rapport?.statut === 'ENVOYE' ||
    (user?.role === 'AGENT' && rapport?.statut === 'SOUMIS');

  const stStyle = rapport ? (STATUT_STYLE[rapport.statut]||STATUT_STYLE.BROUILLON) : null;

  if (loading) return (
    <div className="rp-page" style={{ display:'flex',alignItems:'center',justifyContent:'center',minHeight:'60vh' }}>
      <style>{css}</style>
      <div style={{ textAlign:'center',display:'grid',gap:12 }}>
        <RefreshCw size={24} color={C.terra} className="spin" style={{margin:'0 auto'}}/>
        <span style={{fontSize:'.8rem',color:C.mist}}>Chargement du rapport…</span>
      </div>
    </div>
  );

  return (
    <div className="rp-page">
      <style>{css}</style>

      <motion.div variants={STAGGER} initial="hidden" animate="visible">

        {/* ── TOP BAR ── */}
        <motion.div variants={ITEM} className="rp-topbar">
          <button className="btn-bk" onClick={()=>navigate(`/dossiers/${id}`)}>
            <ArrowLeft size={13}/> Retour au dossier
          </button>
        </motion.div>

        <motion.div variants={ITEM} className="rp-title-row">
          <h1 className="rp-title">Rapport <em>d'expertise</em> — {dossier?.reference}</h1>
          {stStyle && (
            <span className="stag" style={{ background:stStyle.bg, color:stStyle.color }}>
              <span className="stag-dot" style={{background:stStyle.dot}}/>{stStyle.label}
            </span>
          )}
        </motion.div>

        {/* ── ALERTS ── */}
        <AnimatePresence>
          {success && (
            <motion.div className="alert alert-s"
              initial={{opacity:0,height:0}} animate={{opacity:1,height:'auto'}} exit={{opacity:0,height:0}}>
              <CheckCircle size={15} style={{flexShrink:0,marginTop:2}}/>
              {success}
              <button className="alert-x" onClick={()=>setSuccess(null)}>✕</button>
            </motion.div>
          )}
          {error && (
            <motion.div className="alert alert-e"
              initial={{opacity:0,height:0}} animate={{opacity:1,height:'auto'}} exit={{opacity:0,height:0}}>
              <AlertTriangle size={15} style={{flexShrink:0,marginTop:2}}/>
              <pre style={{fontFamily:'inherit',whiteSpace:'pre-wrap',flex:1,fontSize:'.82rem'}}>{error}</pre>
              <button className="alert-x" onClick={()=>setError(null)}>✕</button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── CORRECTIONS BANNER ── */}
        {rapport?.corrections_admin && user?.role==='AGENT' && (
          <motion.div variants={ITEM} className="corrections-banner">
            <div className="cb-icon"><AlertTriangle size={18}/></div>
            <div>
              <div className="cb-title">Corrections demandées par l'administrateur</div>
              <div className="cb-text">{rapport.corrections_admin}</div>
            </div>
          </motion.div>
        )}

        {/* ── BODY ── */}
        <div className="rp-body">

          {/* ── TRAME PRINCIPALE ── */}
          <motion.div variants={ITEM}>
            <div className="trame-card">
              <div className="trame-head">
                <div className="trame-icon"><Edit3 size={16}/></div>
                <span className="trame-title">Trame du rapport</span>
              </div>
              <div className="trame-body">

                {/* SECTIONS ACCORDION */}
                <div className="section-blocks">
                  {SECTIONS.map(section => (
                    <SectionBlock key={section.key}
                      section={section}
                      value={form[section.key]}
                      onChange={val => setField(section.key, val)}
                      disabled={isReadOnly}
                      isOpen={!!openSections[section.key]}
                      onToggle={() => toggleSection(section.key)}
                    />
                  ))}
                </div>

                {/* RÉSULTAT */}
                <div className="resultat-wrap">
                  <div className="r-label">Résultat de l'expertise</div>
                  <div className="resultat-options">
                    {[
                      { val:'EN_ATTENTE', label:'En attente',        icon:'⏳', color:C.gold,      light:'rgba(200,144,42,.12)' },
                      { val:'VALIDE',     label:'Terrain validé',    icon:'✓',  color:C.forestLt,  light:'rgba(47,102,69,.12)'  },
                      { val:'LITIGE',     label:'Litige détecté',    icon:'⚠',  color:C.terraDk,   light:'rgba(194,82,26,.1)'   },
                      { val:'RISQUE',     label:'Risque identifié',  icon:'🔶', color:C.terra,     light:'rgba(194,82,26,.08)'  },
                    ].map(opt => (
                      <button key={opt.val} type="button"
                        className={`r-option${form.resultat===opt.val?' selected':''}`}
                        style={ form.resultat===opt.val ? { background:opt.light, borderColor:opt.color, color:opt.color } : {} }
                        onClick={() => !isReadOnly && setField('resultat', opt.val)}
                        disabled={isReadOnly}>
                        <div className="r-option-dot" style={{ background: form.resultat===opt.val ? opt.color : 'rgba(26,61,40,.15)' }}/>
                        <span style={{lineHeight:1.3}}><span style={{marginRight:6}}>{opt.icon}</span>{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* ACTIONS AGENT */}
                {user?.role==='AGENT' && !isReadOnly && (
                  <div className="trame-actions">
                    <button className="btn-draft" onClick={handleSaveDraft} disabled={!!saving}>
                      {saving==='draft' ? <RefreshCw size={13} className="spin"/> : <Edit3 size={13}/>}
                      Sauvegarder brouillon
                    </button>
                    <button className="btn-submit" onClick={handleSoumettre} disabled={!!saving||!rapport}>
                      {saving==='soumettre' ? <RefreshCw size={13} className="spin"/> : <Send size={13}/>}
                      Soumettre à l'admin
                    </button>
                  </div>
                )}

                {/* WAITING — agent soumis */}
                {user?.role==='AGENT' && rapport?.statut==='SOUMIS' && (
                  <div style={{paddingTop:16,borderTop:'1px solid rgba(26,61,40,.06)'}}>
                    <div className="waiting-state">
                      <div className="ws-icon"><Clock size={20}/></div>
                      <div className="ws-title">En attente de validation</div>
                      <p className="ws-sub">L'administrateur va réviser et valider votre rapport.</p>
                    </div>
                  </div>
                )}

                {/* READ-ONLY hint mandataire */}
                {user?.role==='MANDATAIRE' && rapport?.statut==='ENVOYE' && (
                  <div style={{padding:'14px 0 0',borderTop:'1px solid rgba(26,61,40,.06)',textAlign:'center',color:C.mist,fontSize:'.8rem',lineHeight:1.6}}>
                    <BadgeCheck size={14} style={{display:'inline',marginRight:5,color:C.forestLt}}/> Rapport officiel certifié par TerraTrack
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* ── SIDEBAR ── */}
          <motion.div variants={ITEM} className="side-stack">

            {/* INFOS TERRAIN */}
            <div className="sc">
              <div className="sch">
                <div className="sch-icon" style={{background:'rgba(194,82,26,.1)',color:C.terra}}><MapPin size={15}/></div>
                <span className="sch-title">Terrain concerné</span>
              </div>
              <div className="scb">
                <div className="terrain-strip">
                  {[
                    ['Localisation', dossier?.localisation||'—'],
                    ['Pays',         dossier?.pays||'—'],
                    ['Ville',        dossier?.ville||'—'],
                    ['Superficie',   dossier?.superficie?`${dossier.superficie} m²`:'—'],
                    ['Vendeur',      dossier?.nom_vendeur||'—'],
                    ['Mandataire',   dossier?.mandataire?.email||'—'],
                  ].map(([l,v])=>(
                    <div key={l} className="ts-item">
                      <div className="ts-lbl">{l}</div>
                      <div className="ts-val" style={{fontSize:l==='Mandataire'?'.72rem':undefined}}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* DOCUMENT JOINT */}
            {user?.role!=='MANDATAIRE' && rapport?.statut!=='ENVOYE' && (
              <div className="sc">
                <div className="sch">
                  <div className="sch-icon" style={{background:'rgba(26,61,40,.08)',color:C.forest}}><Paperclip size={15}/></div>
                  <span className="sch-title">Document joint</span>
                </div>
                <div className="scb">
                  <p className="sc-sub">PDF ou Word du rapport signé (optionnel)</p>
                  <DocumentUpload dossierId={id} onUpload={fetchData}/>
                  {rapport?.document_url && (
                    <a className="btn-wide bw-outline" href={rapport.document_url} target="_blank" rel="noreferrer"
                      style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,textDecoration:'none',marginTop:8,padding:'10px',borderRadius:11,border:'1.5px solid rgba(26,61,40,.15)',color:C.forest,fontSize:'.84rem',fontWeight:600}}>
                      <ExternalLink size={13}/> Voir le document joint
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* ADMIN : corrections */}
            {user?.role==='ADMIN' && rapport?.statut==='SOUMIS' && (
              <div className="sc bdr-gold">
                <div className="sch" style={{background:'rgba(200,144,42,.04)'}}>
                  <div className="sch-icon" style={{background:'rgba(200,144,42,.15)',color:C.gold}}><Edit3 size={15}/></div>
                  <span className="sch-title">Demander corrections</span>
                </div>
                <div className="scb">
                  <p className="sc-sub">Renvoyez le rapport à l'agent avec des instructions précises.</p>
                  <textarea className="corr-ta" placeholder="Décrivez les corrections à apporter…"
                    value={corrections} onChange={e=>setCorrections(e.target.value)}/>
                  <button className="btn-wide bw-gold" onClick={handleCorrections} disabled={!corrections.trim()||!!saving}>
                    {saving==='correct'?<RefreshCw size={13} className="spin"/>:<><Send size={13}/> Envoyer les corrections</>}
                  </button>
                </div>
              </div>
            )}

            {/* ADMIN : envoyer au mandataire */}
            {user?.role==='ADMIN' && rapport?.statut==='SOUMIS' && (
              <div className="sc bdr-green">
                <div className="sch" style={{background:'rgba(47,102,69,.04)'}}>
                  <div className="sch-icon" style={{background:'rgba(47,102,69,.12)',color:C.forestLt}}><Send size={15}/></div>
                  <span className="sch-title">Envoyer au mandataire</span>
                </div>
                <div className="scb">
                  <p className="sc-sub">
                    Prévisualisez et envoyez à <strong style={{color:C.forest}}>{dossier?.mandataire?.email||'le mandataire'}</strong>.
                  </p>
                  <button className="btn-wide bw-outline" onClick={()=>setPreviewOpen(true)}>
                    <Eye size={13}/> Prévisualiser le rapport
                  </button>
                  <button className="btn-wide bw-outline" onClick={handlePdf} disabled={!rapport}>
                    <Download size={13}/> Télécharger le PDF
                  </button>
                  <button className="btn-wide bw-green" onClick={()=>setConfirmOpen(true)}>
                    <MailCheck size={13}/> Envoyer au mandataire
                  </button>
                </div>
              </div>
            )}

            {/* RAPPORT ENVOYÉ */}
            {rapport?.statut==='ENVOYE' && (
              <div className="sc bdr-green">
                <div className="sch" style={{background:'rgba(47,102,69,.04)'}}>
                  <div className="sch-icon" style={{background:'rgba(47,102,69,.12)',color:C.forestLt}}><CheckCircle size={15}/></div>
                  <span className="sch-title">Rapport envoyé</span>
                </div>
                <div className="scb">
                  <div className="sent-state">
                    <div className="sent-icon"><MailCheck size={24}/></div>
                    <div className="sent-title">Envoyé avec succès</div>
                    <div className="sent-date">
                      {rapport.envoye_at ? dayjs(rapport.envoye_at).format('DD MMMM YYYY') : '—'}
                    </div>
                    <div className="sent-by">par {rapport.valide_par_nom||'Admin'}</div>
                  </div>
                  <button className="btn-wide bw-outline" style={{marginTop:4}} onClick={handlePdf} disabled={!rapport}>
                    <Download size={13}/> Télécharger le PDF
                  </button>
                </div>
              </div>
            )}

          </motion.div>
        </div>
      </motion.div>

      {/* ── MODALS ── */}
      <AnimatePresence>
        {previewOpen && (
          <PreviewModal
            dossier={dossier} form={form} rapport={rapport}
            onClose={()=>setPreviewOpen(false)}
            onSend={()=>{ setPreviewOpen(false); setConfirmOpen(true); }}
          />
        )}
        {confirmOpen && (
          <ConfirmModal
            dossier={dossier} saving={saving==='envoyer'}
            onClose={()=>setConfirmOpen(false)}
            onConfirm={handleEnvoyer}
          />
        )}
      </AnimatePresence>
    </div>
  );
}